import { useAnalytics } from '@app/analytics/use-analytics';
import { MenuNavButton, NavButton } from '@app/components';
import { TeamInvitationBanner } from '@app/components/team-invitation-banner';
import {
  selectOrganizationInfo,
  selectOrganizationPaymentPlan,
} from '@app/routes/organization/selectors';
import {
  selectIsHelpMenuOpen,
  selectIsLanguagePickerOpen,
  selectOrganizationId,
  selectUserProfile,
  selectUserProfileError,
} from '@app/store/selectors';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { DashboardFeatureFlagTest } from '@hopin-team/component-dashboard-feature-flag-test';
import { getLogoutUrl } from '@hopin-team/dashboard-constants';
import { generateOrganizationSlug } from '@hopin-team/dashboard-shared';
import {
  useReadDashboardSharedState,
  useWriteDashboardSharedState,
} from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import { getFromTheme } from '@hopin-team/ui-helper-deep-branding';
import {
  LanguagePicker,
  LanguagePickerProps,
} from '@hopin-team/ui-language-picker';
import { Logo } from '@hopin-team/ui-logo';
import {
  Menu,
  MenuContent,
  MenuSelect,
  MenuSelectLink,
} from '@hopin-team/ui-menu';
import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { getMenuItems, MenuItem } from '../nav-bar/menu-items';
import { Pendo } from '../nav-bar/pendo';
import { OrgSwitcher } from '../org-switcher';

const MenuItems = ({
  items,
  onClick,
}: {
  items: MenuItem[];
  onClick: (item: MenuItem) => void;
}) => (
  <>
    {items.map(
      (props, index) =>
        !props.disabled && (
          <NavButton {...props} key={index} onClick={() => onClick(props)} />
        ),
    )}
  </>
);

const AdditionalMenuItems = ({ items }: { items: MenuItem[] }) => (
  <>
    {items.map((props, index) => (
      <NavButton
        {...props}
        data-key={index}
        key={index}
        withSpacing={index === 0}
      />
    ))}
  </>
);

export const NavBarV2 = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const organizationId = useAppSelector(selectOrganizationId);
  const isLanguagePickerOpen = useAppSelector(selectIsLanguagePickerOpen);
  const isHelpMenuOpen = useAppSelector(selectIsHelpMenuOpen);

  const userProfile = useAppSelector(selectUserProfile);
  const userProfileError = useAppSelector(selectUserProfileError);
  const paymentPlan = useAppSelector(selectOrganizationPaymentPlan);
  const currentOrganization = useAppSelector(selectOrganizationInfo);
  const navigate = useNavigate();

  const setLocale = useWriteDashboardSharedState('i18n_locale');
  const flags = useReadDashboardSharedState('feature_flags');
  const { track } = useAnalytics();

  const { organizationSlug } = useParams<{ organizationSlug: string }>();

  useEffect(() => {
    if (userProfileError) {
      throw new Error(userProfileError);
    }
  }, [userProfileError]);

  const onClickHandler = ({ label, name }: MenuItem) => {
    if (label === t('navbar.analytics'))
      track('ola_analytics_tab_click', { appName: 'RCE Org level Analytics' });

    track('org_dash_menu_item_clicked', {
      menuItem: name,
      appName: 'RCE Organizer Dashboard',
    });
  };

  const { menuItems, additionalMenuItems } = getMenuItems({
    organizationSlug,
    features: currentOrganization?.features,
    isFreePlan: paymentPlan?.planName?.toLowerCase() === 'free',
    flags,
    t,
  });

  const navigateToOrg = (org: {
    id: string;
    name: string;
    image: string;
    paymentPlan: string;
    planNameLocale: string;
    largeImage: string;
  }) => {
    const slug = generateOrganizationSlug(org.id, org.name);
    dispatch({
      type: '[nav-bar] organization selected',
      payload: {
        isOrgSelected: true,
        selectedOrgInfo: org,
      },
    });
    navigate(`/org/${slug}`);
  };

  const manageOrg = () => {
    if (organizationId && currentOrganization?.name) {
      const slug = generateOrganizationSlug(
        organizationId || '',
        currentOrganization.name,
      );
      navigate(`/org/${slug}/manage`);
    }
  };

  const logout = () => {
    location.assign(getLogoutUrl());
  };

  const selectProfile = () => {
    dispatch({
      type: '[nav-bar] organization selected',
      payload: { isOrgSelected: false },
    });
    navigate(`/attendee`);
  };

  const selectVendorBooth = ({ id }: { id: string }) => {
    dispatch({
      type: '[nav-bar] organization selected',
      payload: { isOrgSelected: false },
    });
    navigate(`/vendor/${id}`);
  };

  const manageProfile = () => {
    dispatch({
      type: '[nav-bar] organization selected',
      payload: { isOrgSelected: false },
    });
    navigate(`/attendee/profile`);
  };

  const createEvent = () => {
    navigate(`/create-event-wizard`);
  };

  const createOrg = () => {
    navigate(`/org/new?performed_from=org%20switcher`);
  };

  const onChangeLanguage = (selectedLocale: string) => {
    const newLocale = flags?.['dashboard-four-character-locales']
      ? selectedLocale
      : selectedLocale.substring(0, 2);
    setLocale(newLocale);
    dispatch({
      type: '[nav-bar] update language picker open state',
      payload: { isLanguagePickerOpen: false },
    });
  };

  return (
    <>
      <LanguagePicker
        languages={
          flags?.['dashboard-four-character-locales']
            ? undefined
            : [
                'en-US',
                'de-DE',
                'es-419',
                'fr-FR',
                'it-IT',
                'ja-JP',
                'ko-KR',
                'pt-BR',
                'pl-PL',
                'nl-NL',
              ]
        }
        isShowing={isLanguagePickerOpen}
        title={t('languagePicker.title')}
        saveButtonText={t('languagePicker.save')}
        cancelButtonText={t('languagePicker.cancel')}
        defaultLanguage={
          i18n.language as LanguagePickerProps['defaultLanguage']
        }
        onChangeLanguage={onChangeLanguage}
        onCancel={() =>
          dispatch({
            type: '[nav-bar] update language picker open state',
            payload: { isLanguagePickerOpen: false },
          })
        }
      />
      <nav
        className="sticky top-0 w-full z-[101] bg-white border-b"
        style={{
          borderBottom: `1px solid ${getFromTheme('COLOR.GREY["300"]')}`,
        }}
      >
        <TeamInvitationBanner />
        <div className="pl-6 pr-0 max-w-[90rem] mx-auto transition-[padding-left,padding-right] duration-200 ease-in-out">
          <div className="flex h-16 items-stretch">
            <Link to="/" className="flex items-center mr-10">
              <Logo
                type={'ringcentral-default'}
                scale={3}
                className="self-center"
              />
            </Link>
            <MenuItems items={menuItems} onClick={onClickHandler} />
            <AdditionalMenuItems items={additionalMenuItems} />
            <Menu
              isShowing={isHelpMenuOpen}
              isInline
              isDismissible
              onClose={() => {
                // Restore focus before dismissing the focused menu item.
                // Keep focus on another control when it dismisses the menu.
                if (
                  document.activeElement?.closest(
                    '[role="menu"][aria-labelledby="help-menu"]',
                  )
                ) {
                  document.getElementById('help-menu')?.focus();
                }
                dispatch({
                  type: '[nav-bar] update help menu open state',
                  payload: { isHelpMenuOpen: false },
                });
              }}
              ml={additionalMenuItems.length === 0 ? 'auto' : undefined}
            >
              <MenuNavButton
                id="help-menu"
                trailingIcon={isHelpMenuOpen ? 'arrow-up' : 'arrow-down'}
                leadingIcon="info-outlined"
                onClick={() =>
                  dispatch({
                    type: '[nav-bar] update help menu open state',
                    payload: { isHelpMenuOpen: !isHelpMenuOpen },
                  })
                }
                color="grey-600"
                size="small"
                aria-haspopup
                aria-expanded={isHelpMenuOpen}
                label={t('navbar.help')}
                className={isHelpMenuOpen ? 'open' : undefined}
              />
              <MenuContent p={1} size="small">
                <MenuSelect labelledById="help-menu">
                  <MenuSelectLink
                    leadingIcon="megaphone"
                    itemUrl="https://support.ringcentral.com/release-notes/mvp/video/events.html"
                    target="_blank"
                    label={t('navbar.helpMenu.whatsNew')}
                    py={1}
                    px={2}
                    my={1}
                  />
                  <MenuSelectLink
                    leadingIcon="question-mark-fill"
                    itemUrl="https://support.ringcentral.com/events.html"
                    target="_blank"
                    label={t('navbar.helpMenu.knowledgeCenter')}
                    py={1}
                    px={2}
                    my={1}
                  />
                </MenuSelect>
              </MenuContent>
            </Menu>
            <OrgSwitcher
              onSelectOrg={navigateToOrg}
              onLogOut={logout}
              onSelectProfile={selectProfile}
              onCreateEvent={createEvent}
              onCreateOrg={createOrg}
              onSelectVendorBooth={selectVendorBooth}
              manageOrg={manageOrg}
              manageProfile={manageProfile}
            />
          </div>
        </div>
      </nav>
      <DashboardFeatureFlagTest />
      {userProfile &&
        userProfile.id &&
        userProfile.email &&
        organizationId &&
        currentOrganization?.paymentPlan?.planName && (
          <Pendo
            userExternalId={userProfile.id}
            userEmail={userProfile.email}
            organizationExternalId={organizationId}
            paymentPlan={currentOrganization.paymentPlan.planName}
          />
        )}
    </>
  );
};
